import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function TermsOfService() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-dark-100 border-b border-dark-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-vapour-600 hover:text-vapour-900 mr-3"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-semibold text-vapour-900">Terms of Service</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-dark-200 p-8">
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-vapour-900 mb-4">Terms of Service</h1>
            <p className="text-vapour-600 text-lg">
              Effective Date: September 4, 2025 | Last Updated: September 4, 2025
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-vapour-700 mb-4">
                Welcome to VapourVape Rewards ("we," "our," or "us"). These Terms of Service ("Terms") govern your use of our loyalty program application, website, and related services (collectively, the "Service").
              </p>
              <p className="text-vapour-700 mb-4">
                By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these terms, then you may not access the Service.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium">
                  <strong>Important:</strong> Please read these Terms carefully before using our Service. Your use constitutes acceptance of these Terms.
                </p>
              </div>
            </section>

            {/* Definitions */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 text-vapour-700 space-y-2">
                <li><strong>"Service"</strong> refers to the VapourVape Rewards loyalty program application and related services</li>
                <li><strong>"User," "you," or "your"</strong> refers to the individual accessing or using the Service</li>
                <li><strong>"Account"</strong> refers to your registered user account with the Service</li>
                <li><strong>"Stamps"</strong> refers to the digital loyalty points earned through purchases</li>
                <li><strong>"Rewards"</strong> refers to benefits earned through the loyalty program</li>
                <li><strong>"Content"</strong> refers to all information, data, text, images, and other materials</li>
              </ul>
            </section>

            {/* Eligibility */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">3. Eligibility</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">3.1 Age Requirements</h3>
              <p className="text-vapour-700 mb-4">
                You must be at least 18 years old to use this Service. By using the Service, you represent and warrant that you are at least 18 years of age.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">3.2 Legal Capacity</h3>
              <p className="text-vapour-700 mb-4">
                You must have the legal capacity to enter into binding agreements. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">3.3 Geographic Restrictions</h3>
              <p className="text-vapour-700">
                The Service is intended for users in [Your Geographic Area]. Use of the Service may be restricted or prohibited in certain jurisdictions.
              </p>
            </section>

            {/* Account Registration */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">4. Account Registration and Security</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">4.1 Account Creation</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>You must provide accurate, current, and complete information during registration</li>
                <li>You are responsible for maintaining the accuracy of your account information</li>
                <li>Each person may only create one account</li>
                <li>You must not create accounts using false or misleading information</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">4.2 Account Security</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must notify us immediately of any unauthorized use of your account</li>
                <li>We are not liable for losses resulting from unauthorized use of your account</li>
                <li>You may not share your account with others or allow others to use your account</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">4.3 Account Termination</h3>
              <p className="text-vapour-700">
                You may terminate your account at any time. We may suspend or terminate accounts that violate these Terms or for other legitimate business reasons.
              </p>
            </section>

            {/* Loyalty Program */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">5. Loyalty Program Terms</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">5.1 Earning Stamps</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Stamps are earned through qualifying purchases at VapourVape locations</li>
                <li>One stamp is typically earned per qualifying purchase</li>
                <li>Staff may award additional stamps at their discretion for special promotions</li>
                <li>Stamps have no cash value and cannot be transferred between accounts</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">5.2 Redeeming Rewards</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Rewards can be redeemed when you accumulate 6 stamps</li>
                <li>Redemption must occur at a VapourVape location</li>
                <li>Rewards cannot be combined with other offers unless specified</li>
                <li>Rewards are subject to availability and may be substituted</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">5.3 Stamp Expiration</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Stamps do not expire as long as your account remains active</li>
                <li>Inactive accounts (no activity for 12+ months) may have stamps expire</li>
                <li>We will provide 30 days notice before expiring stamps due to inactivity</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">5.4 Program Changes</h3>
              <p className="text-vapour-700">
                We reserve the right to modify the loyalty program terms, rewards, or stamp requirements with reasonable notice to users.
              </p>
            </section>

            {/* Acceptable Use */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">6. Acceptable Use Policy</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">6.1 Permitted Uses</h3>
              <p className="text-vapour-700 mb-4">You may use the Service to:</p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Participate in the VapourVape loyalty program</li>
                <li>Track your stamps and rewards</li>
                <li>Receive promotional communications (with consent)</li>
                <li>Access customer support</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">6.2 Prohibited Uses</h3>
              <p className="text-vapour-700 mb-4">You may not:</p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Use the Service for any unlawful purpose or in violation of applicable laws</li>
                <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
                <li>Create multiple accounts to circumvent program limitations</li>
                <li>Use automated systems (bots, scripts) to interact with the Service</li>
                <li>Reverse engineer, decompile, or attempt to extract source code</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Transmit viruses, malware, or other harmful code</li>
                <li>Harass, abuse, or harm other users or staff</li>
                <li>Violate any intellectual property rights</li>
                <li>Engage in fraudulent activities or misrepresent information</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">7. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">7.1 Our Rights</h3>
              <p className="text-vapour-700 mb-4">
                The Service and its original content, features, and functionality are owned by VapourVape and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">7.2 Limited License</h3>
              <p className="text-vapour-700 mb-4">
                We grant you a limited, non-exclusive, non-transferable license to use the Service for its intended purpose. This license does not include any right to:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Resell or make commercial use of the Service</li>
                <li>Distribute, publicly perform, or publicly display any part of the Service</li>
                <li>Modify or create derivative works based on the Service</li>
                <li>Use any data mining, robots, or similar data gathering methods</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">7.3 Trademarks</h3>
              <p className="text-vapour-700">
                VapourVape, the VapourVape logo, and other marks are trademarks of VapourVape. You may not use these trademarks without our prior written consent.
              </p>
            </section>

            {/* Privacy and Data */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">8. Privacy and Data Protection</h2>
              <p className="text-vapour-700 mb-4">
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information when you use the Service. By using the Service, you agree to the collection and use of information in accordance with our Privacy Policy.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  <strong>Privacy Policy:</strong> Please review our comprehensive Privacy Policy for detailed information about our data practices.
                </p>
              </div>
            </section>

            {/* Disclaimers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">9. Disclaimers and Warranties</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">9.1 Service Availability</h3>
              <p className="text-vapour-700 mb-4">
                We strive to maintain the Service's availability but cannot guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or technical issues.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">9.2 "As Is" Basis</h3>
              <p className="text-vapour-700 mb-4">
                The Service is provided on an "as is" and "as available" basis. We make no warranties, expressed or implied, regarding the Service's reliability, accuracy, or fitness for a particular purpose.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">9.3 Third-Party Services</h3>
              <p className="text-vapour-700">
                The Service may integrate with third-party services (Google Wallet, payment processors). We are not responsible for the availability, accuracy, or reliability of these third-party services.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">10. Limitation of Liability</h2>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-medium">
                  <strong>Important Legal Notice:</strong> Please read this section carefully as it limits our liability.
                </p>
              </div>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">10.1 Damages Limitation</h3>
              <p className="text-vapour-700 mb-4">
                To the maximum extent permitted by law, VapourVape shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Loss of profits, data, or business opportunities</li>
                <li>Service interruptions or delays</li>
                <li>Unauthorized access to your account</li>
                <li>Errors or omissions in the Service</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">10.2 Maximum Liability</h3>
              <p className="text-vapour-700">
                Our total liability for any claims arising from or related to the Service shall not exceed the value of rewards earned in your account or $100, whichever is less.
              </p>
            </section>

            {/* Indemnification */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">11. Indemnification</h2>
              <p className="text-vapour-700 mb-4">
                You agree to indemnify, defend, and hold harmless VapourVape and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 space-y-2">
                <li>Your use or misuse of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Any content you submit or transmit through the Service</li>
              </ul>
            </section>

            {/* Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">12. Termination</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">12.1 Termination by You</h3>
              <p className="text-vapour-700 mb-4">
                You may terminate your account at any time by contacting customer support or using the account deletion feature in the app.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">12.2 Termination by Us</h3>
              <p className="text-vapour-700 mb-4">
                We may suspend or terminate your account immediately, without prior notice, for:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Violation of these Terms</li>
                <li>Fraudulent or abusive behavior</li>
                <li>Legal or regulatory requirements</li>
                <li>Extended periods of inactivity</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">12.3 Effect of Termination</h3>
              <p className="text-vapour-700">
                Upon termination, your right to use the Service ceases immediately. Unused stamps and rewards may be forfeited unless otherwise specified.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">13. Governing Law and Dispute Resolution</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">13.1 Governing Law</h3>
              <p className="text-vapour-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to conflict of law principles.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">13.2 Dispute Resolution</h3>
              <p className="text-vapour-700 mb-4">
                We encourage resolving disputes through direct communication. If a dispute cannot be resolved informally, it shall be resolved through binding arbitration in accordance with the rules of [Arbitration Organization].
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">13.3 Class Action Waiver</h3>
              <p className="text-vapour-700">
                You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
              </p>
            </section>

            {/* General Provisions */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">14. General Provisions</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">14.1 Entire Agreement</h3>
              <p className="text-vapour-700 mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and VapourVape regarding the Service.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">14.2 Severability</h3>
              <p className="text-vapour-700 mb-4">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">14.3 Waiver</h3>
              <p className="text-vapour-700 mb-4">
                Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">14.4 Assignment</h3>
              <p className="text-vapour-700">
                You may not assign or transfer these Terms without our written consent. We may assign these Terms without restriction.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">15. Changes to Terms</h2>
              <p className="text-vapour-700 mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of material changes by:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Posting updated Terms on our website</li>
                <li>Sending email notifications to registered users</li>
                <li>Displaying in-app notifications</li>
              </ul>
              <p className="text-vapour-700">
                Your continued use of the Service after changes become effective constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">16. Contact Information</h2>
              <p className="text-vapour-700 mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              
              <div className="bg-vapour-50 border border-vapour-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-vapour-900 mb-3">VapourVape Legal Team</h3>
                <div className="space-y-2 text-vapour-700">
                  <p><strong>Email:</strong> legal@vapourvape.com</p>
                  <p><strong>Support:</strong> support@vapourvape.com</p>
                  <p><strong>Phone:</strong> +1234567890</p>
                  <p><strong>Address:</strong> [Your Business Address]</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-vapour-200">
                  <p className="text-sm text-vapour-600">
                    <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM
                  </p>
                </div>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-dark-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-sm text-vapour-600">
                <p>Last updated: September 4, 2025</p>
                <p>Version 1.0</p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-vapour-600 hover:text-vapour-900 text-sm underline"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="text-vapour-600 hover:text-vapour-900 text-sm underline"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  )
}